import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useQuery } from "../../../hooks/useQuery";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { Modal } from "../../Modal";
import { Text } from "../../Text";
import { CopyableValue } from "../CopyableValue";
import { DangerSection } from "../DangerSection";
import {
  PanelCard,
  PanelContentBox,
  PanelHeader,
  PanelShell,
} from "../SettingsPanel";
import { ModalContent, ModalFooter } from "../shared";
import { ExistingSsoProvider } from "./ExistingSsoProvider";
import { NewSsoProvider } from "./NewSsoProvider";

const SessionSection = styled(PanelContentBox)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export function SSOSettings() {
  const z = useZero();
  const [providers] = useQuery(queries.ssoProviders());
  const canManageSSO = useHasAccess("tenant:sso_manage");
  const provider = providers[0];
  // Lives here so a token minted while registering a provider survives the
  // swap from NewSsoProvider to ExistingSsoProvider.
  const [scimToken, setScimToken] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  if (!canManageSSO) {
    return (
      <PanelShell>
        <PanelCard>
          <PanelHeader>
            <Text variant="display-md">Single sign-on</Text>
            <div style={{ marginTop: 8 }}>
              <Text variant="text-regular" color="var(--text-secondary)">
                Only admins can configure SSO
              </Text>
            </div>
          </PanelHeader>
        </PanelCard>
      </PanelShell>
    );
  }

  return (
    <PanelShell>
      <PanelCard>
        <PanelHeader>
          <Text variant="display-md">Single sign-on</Text>
          <div style={{ marginTop: 8 }}>
            <Text variant="text-regular" color="var(--text-secondary)">
              Configure single sign-on for your tenant. Group mappings sync
              roles and space memberships on each sign-in.
            </Text>
          </div>
        </PanelHeader>

        <PanelContentBox>
          {provider ? (
            <ExistingSsoProvider
              provider={provider}
              onGeneratedScimToken={setScimToken}
            />
          ) : (
            <NewSsoProvider onGeneratedScimToken={setScimToken} />
          )}
        </PanelContentBox>

        <SessionSection>
          <div>
            <Text variant="text-regular-md">Sessions</Text>
            <div style={{ marginTop: 4 }}>
              <Text variant="text-regular" color="var(--text-secondary)">
                Force all users to re-authenticate
              </Text>
            </div>
          </div>
          <Button
            variant="subtle"
            size="l"
            onClick={() => setConfirmRevoke(true)}
          >
            Revoke all sessions
          </Button>
        </SessionSection>

        {provider && (
          <DangerSection
            style={{ width: "100%", maxWidth: 648, marginTop: 24 }}
            title="Delete SSO provider"
            description="Users will no longer be able to sign in with this SSO provider. This can’t be undone."
            actionLabel="Delete SSO provider"
            confirm={{
              confirmLabel: "Delete",
              onConfirm: () => {
                z.mutate(sharedMutators.deleteSSOProvider({ id: provider.id }));
                setScimToken(null);
              },
            }}
          />
        )}
        {scimToken && (
          <Modal open onClose={() => setScimToken(null)}>
            <ModalContent>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Text variant="text-regular-md">SCIM token created</Text>
                <Text variant="text-regular" color="var(--text-secondary)">
                  Copy this token now — it won’t be shown again.
                </Text>
              </div>
              <CopyableValue value={scimToken}>
                <Text
                  variant="text-regular"
                  color="var(--text-secondary)"
                  style={{ flex: 1 }}
                >
                  {scimToken}
                </Text>
              </CopyableValue>
              <ModalFooter>
                <Button size="l" onClick={() => setScimToken(null)}>
                  Done
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
        <ConfirmModal
          open={confirmRevoke}
          onClose={() => setConfirmRevoke(false)}
          onConfirm={() => {
            z.mutate(sharedMutators.revokeAllSessions({}));
            setConfirmRevoke(false);
          }}
          title="Revoke all sessions"
          description="All other users will be signed out and must re-authenticate. Your current session will not be affected — sign out manually if you also need to re-authenticate."
          confirmLabel="Revoke all"
          destructive
        />
      </PanelCard>
    </PanelShell>
  );
}
